import { enumObject } from './enum-helpers';
import type { ScopeType } from './enums';
import { Permissions as P, type PermissionCode } from './permissions';

/** Seeded role codes (§7.1 core roles and optional roles). Roles are DB rows and extensible. */
export const ROLE_CODES = [
  'FACULTY',
  'DEPARTMENT_CHAIR',
  'DEAN',
  'HR',
  'ACCOUNTING',
  'SYSTEM_ADMIN',
  'VPAA',
  'REGISTRAR',
  'COLLEGE_SECRETARY',
  'ATTENDANCE_VERIFIER',
  'IT_AUDITOR',
  'EXECUTIVE_VIEWER',
  'PAYROLL_INTEGRATION',
  'SIS_INTEGRATION',
  'HRIS_INTEGRATION',
  'TIME_CLOCK_INTEGRATION',
  'MEETING_INTEGRATION',
] as const;
export type RoleCode = (typeof ROLE_CODES)[number];
export const RoleCode = enumObject(ROLE_CODES);

export interface RoleDefinition {
  readonly code: RoleCode;
  readonly name: string;
  readonly description: string;
  /** The scope type an assignment of this role normally uses. */
  readonly defaultScope: ScopeType;
  readonly permissions: readonly PermissionCode[];
}

/**
 * Default role → permission matrix (seed data). Administrators with `role.manage`
 * may change these mappings at runtime; changes are audited as PERMISSIONS events.
 */
export const DEFAULT_ROLE_DEFINITIONS: readonly RoleDefinition[] = [
  {
    code: 'FACULTY',
    name: 'Faculty',
    description: 'Faculty member: views own schedule, clocks in/out, submits requests.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_SELF,
      P.SCHEDULE_READ_SELF,
      P.ATTENDANCE_CLOCK_IN,
      P.ATTENDANCE_CLOCK_OUT,
      P.ATTENDANCE_READ_SELF,
      P.ATTENDANCE_CERTIFY,
      P.ATTENDANCE_CORRECT_REQUEST,
      P.PROOF_UPLOAD,
      P.MAKEUP_REQUEST,
      P.SUBSTITUTION_REQUEST,
      // §64 Overload: "Faculty/Chair → Dean → HR / Academic Affairs".
      P.OVERLOAD_REQUEST,
      P.DUTY_REQUEST,
      P.LEAVE_REQUEST,
      P.REPORT_READ_SELF,
      P.DEVICE_REGISTER_SELF,
      P.EVENT_CHECK_IN,
    ],
  },
  {
    code: 'DEPARTMENT_CHAIR',
    name: 'Department Chair / Program Head',
    description: 'Reviews and approves attendance and requests for an assigned department.',
    defaultScope: 'DEPARTMENT',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.ATTENDANCE_VERIFY,
      P.ATTENDANCE_CORRECT_APPROVE,
      P.PROOF_VERIFY,
      P.MAKEUP_APPROVE,
      P.SUBSTITUTION_REQUEST,
      P.SUBSTITUTION_APPROVE,
      P.OVERLOAD_REQUEST,
      P.OVERLOAD_APPROVE,
      P.DUTY_APPROVE,
      P.DUTY_MANAGE,
      P.REPORT_READ_DEPARTMENT,
      P.REPORT_EXPORT,
      P.DASHBOARD_READ_DEPARTMENT,
      P.EVENT_READ,
      P.EVENT_MANAGE,
    ],
  },
  {
    code: 'DEAN',
    name: 'Dean',
    description: 'College-level review and approval.',
    defaultScope: 'COLLEGE',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.ATTENDANCE_VERIFY,
      P.ATTENDANCE_APPROVE,
      P.ATTENDANCE_CORRECT_APPROVE,
      P.PROOF_VERIFY,
      P.MAKEUP_APPROVE,
      P.SUBSTITUTION_APPROVE,
      P.OVERLOAD_APPROVE,
      P.DUTY_APPROVE,
      P.LEAVE_APPROVE,
      P.REPORT_READ_DEPARTMENT,
      P.REPORT_EXPORT,
      P.DASHBOARD_READ_DEPARTMENT,
      P.EVENT_READ,
      P.EVENT_MANAGE,
    ],
  },
  {
    code: 'HR',
    name: 'Human Resources',
    description: 'Manages employment profiles, attendance policy, leave and payroll cut-offs.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.USER_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.FACULTY_MANAGE,
      P.EMPLOYMENT_TYPE_MANAGE,
      P.SCHEDULE_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.ATTENDANCE_VERIFY,
      P.ATTENDANCE_APPROVE,
      P.ATTENDANCE_FINALIZE,
      P.ATTENDANCE_RECORD_MANUAL,
      P.ATTENDANCE_CORRECT_APPROVE,
      P.ATTENDANCE_POST_LOCK_ADJUST,
      P.ATTENDANCE_LOCATION_READ,
      // Final overload step (§64 "HR / Academic Affairs").
      P.OVERLOAD_APPROVE,
      P.LEAVE_APPROVE,
      P.LEAVE_MANAGE,
      P.DUTY_MANAGE,
      P.CALENDAR_MANAGE,
      P.POLICY_MANAGE,
      P.PAYROLL_PERIOD_MANAGE,
      P.PAYROLL_PERIOD_REOPEN,
      P.PAYROLL_REPORT_READ,
      P.PAYROLL_REPORT_EXPORT,
      P.REPORT_READ_DEPARTMENT,
      P.REPORT_EXPORT,
      P.DASHBOARD_READ_DEPARTMENT,
      P.DEVICE_READ,
      P.DEVICE_MANAGE,
      P.DISPLAY_READ,
      P.DISPLAY_MANAGE,
      P.CARD_READ,
      P.CARD_MANAGE,
      P.EVENT_READ,
      P.EVENT_MANAGE,
    ],
  },
  {
    code: 'ACCOUNTING',
    name: 'Accounting / Payroll Encoder',
    description: 'Views and exports payroll-ready reports; marks cut-offs processed.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.PAYROLL_REPORT_READ,
      P.PAYROLL_REPORT_EXPORT,
      P.PAYROLL_REPORT_MARK_PROCESSED,
    ],
  },
  {
    code: 'SYSTEM_ADMIN',
    name: 'System Administrator',
    description: 'Manages users, roles, organization data, locations, settings and workflows.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.USER_READ,
      P.USER_MANAGE,
      P.ROLE_READ,
      P.ROLE_MANAGE,
      P.ORG_MANAGE,
      P.LOCATION_MANAGE,
      P.GEOFENCE_MANAGE,
      P.ACADEMIC_MANAGE,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.SCHEDULE_MANAGE,
      P.SCHEDULE_IMPORT,
      P.FACULTY_LOAD_MANAGE,
      P.CALENDAR_MANAGE,
      P.WORKFLOW_MANAGE,
      P.SETTINGS_MANAGE,
      P.AUDIT_READ,
      P.SYSTEM_HEALTH_READ,
      P.DEVICE_READ,
      P.DEVICE_MANAGE,
      P.DISPLAY_READ,
      P.DISPLAY_MANAGE,
      P.CARD_READ,
      P.CARD_MANAGE,
      P.INTEGRATION_READ,
      P.INTEGRATION_MANAGE,
    ],
  },
  {
    code: 'VPAA',
    name: 'Vice President for Academic Affairs',
    description: 'Institution-wide academic oversight and approvals.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.ATTENDANCE_APPROVE,
      P.MAKEUP_APPROVE,
      P.SUBSTITUTION_APPROVE,
      P.OVERLOAD_APPROVE,
      P.REPORT_READ_DEPARTMENT,
      P.REPORT_EXPORT,
      P.DASHBOARD_READ_DEPARTMENT,
      P.EVENT_READ,
      P.EVENT_MANAGE,
    ],
  },
  {
    code: 'REGISTRAR',
    name: 'Registrar',
    description: 'Maintains academic periods, courses, sections and schedules.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.ACADEMIC_MANAGE,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.SCHEDULE_MANAGE,
      P.SCHEDULE_IMPORT,
      P.FACULTY_LOAD_MANAGE,
    ],
  },
  {
    code: 'COLLEGE_SECRETARY',
    name: 'College Secretary',
    description: 'Maintains schedules and faculty loads for a college.',
    defaultScope: 'COLLEGE',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.SCHEDULE_READ_DEPARTMENT,
      P.SCHEDULE_MANAGE,
      P.SCHEDULE_IMPORT,
      P.FACULTY_LOAD_MANAGE,
      P.REPORT_READ_DEPARTMENT,
    ],
  },
  {
    code: 'ATTENDANCE_VERIFIER',
    name: 'Attendance Verifier',
    description: 'Verifies attendance and class proof within an assigned scope.',
    defaultScope: 'DEPARTMENT',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.ATTENDANCE_VERIFY,
      P.PROOF_VERIFY,
      P.DASHBOARD_READ_DEPARTMENT,
    ],
  },
  {
    code: 'IT_AUDITOR',
    name: 'IT Auditor',
    description: 'Read-only access to audit logs, users and role mappings.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.AUDIT_READ, P.USER_READ, P.ROLE_READ, P.SYSTEM_HEALTH_READ, P.DEVICE_READ, P.DISPLAY_READ, P.CARD_READ, P.INTEGRATION_READ],
  },
  {
    code: 'EXECUTIVE_VIEWER',
    name: 'Read-Only Executive Viewer',
    description: 'Read-only dashboards and reports.',
    defaultScope: 'INSTITUTION',
    permissions: [
      P.REFERENCE_READ,
      P.FACULTY_READ_DEPARTMENT,
      P.ATTENDANCE_READ_DEPARTMENT,
      P.REPORT_READ_DEPARTMENT,
      P.DASHBOARD_READ_DEPARTMENT,
      P.EVENT_READ,
    ],
  },
  {
    code: 'PAYROLL_INTEGRATION',
    name: 'Payroll system (integration)',
    description: 'For the integration client of the payroll system (Phase 3B): reads the payroll hand-off of locked cut-offs and confirms them processed.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.PAYROLL_REPORT_READ, P.PAYROLL_REPORT_EXPORT, P.PAYROLL_REPORT_MARK_PROCESSED],
  },
  {
    code: 'SIS_INTEGRATION',
    name: 'Student information system (integration)',
    description:
      'For the integration client of the SIS (Phase 3C): keeps academic years, terms, courses, sections, faculty loads and class schedules in step with the SIS.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.ACADEMIC_MANAGE, P.FACULTY_READ_DEPARTMENT, P.SCHEDULE_READ_DEPARTMENT, P.SCHEDULE_MANAGE, P.SCHEDULE_IMPORT, P.FACULTY_LOAD_MANAGE],
  },
  {
    code: 'HRIS_INTEGRATION',
    name: 'HR information system (integration)',
    description:
      'For the integration client of the HRIS (Phase 3D): keeps faculty and employment data in step (separation ends access), records leave approved in the HRIS and receives leave decided here, and brings holidays and suspensions from the HR calendar.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.FACULTY_READ_DEPARTMENT, P.FACULTY_MANAGE, P.LEAVE_SYNC, P.CALENDAR_MANAGE],
  },
  {
    code: 'TIME_CLOCK_INTEGRATION',
    name: 'Biometric time clocks (integration)',
    description:
      'For the integration client that collects the punches of biometric time clocks (Phase 3H): sends them for registered terminals; each one is recorded at its terminal and time and always verified by a person.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.ATTENDANCE_TIME_CLOCK_SYNC],
  },
  {
    code: 'MEETING_INTEGRATION',
    name: 'Meeting platform (integration)',
    description:
      'For the integration client that relays meeting-platform events (Phase 3I): sends faculty members’ joins and leaves of online meetings, kept as evidence for their online classes; never clocks anyone in or out.',
    defaultScope: 'INSTITUTION',
    permissions: [P.REFERENCE_READ, P.ATTENDANCE_MEETING_SYNC],
  },
];
